<script setup lang="ts">
import { copyTextToClipboard, formatDateTime, useMobileView } from '@metapic-apps/shared'
import { computed, ref, toRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useGetAffiliateLink, useUpdateAffiliateLink } from '@/api/affiliate-links.api'
import CopyIcon from '@/assets/icons/copy.svg?component'
import CloseIcon from '@/assets/icons/remove.svg?component'
import CreatorAffiliateLinkBreakdown from '@/components/CreatorDetail/CreatorAffiliateLinkBreakdown.vue'
import CreatorAffiliateLinkClickReport from '@/components/CreatorDetail/CreatorAffiliateLinkClickReport.vue'
import CreatorAffiliateLinkClicksCpc from '@/components/CreatorDetail/CreatorAffiliateLinkClicksCpc.vue'
import { notification } from '@/lib/helpers'
import { BoxComponent, ButtonComponent, DrawerComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  linkId: number
}>()

const { isMobileView } = useMobileView()

const emit = defineEmits<{
  saved: []
}>()

const isOpen = defineModel<boolean>({ default: false })

const route = useRoute()
const router = useRouter()

type DetailsTab = 'options' | 'performance' | 'click-report' | 'clicks-cpc'
const linkTabParams: Record<DetailsTab, string | undefined> = {
  options: 'options',
  performance: undefined,
  'click-report': 'click-report',
  'clicks-cpc': 'clicks-cpc',
}
const activeTab = computed<DetailsTab>({
  get: () => {
    if (
      route.query.linkTab === 'options' ||
      route.query.linkTab === 'performance' ||
      route.query.linkTab === 'click-report' ||
      route.query.linkTab === 'clicks-cpc'
    ) {
      return route.query.linkTab
    }
    return 'performance'
  },
  set: (tab) => {
    void router.push({ query: { ...route.query, linkTab: linkTabParams[tab] } })
  },
})
const pendingLink = ref<string | undefined>(undefined)

const linkIdRef = toRef(props, 'linkId')
const { data: link, isLoading, error, refetch } = useGetAffiliateLink(linkIdRef)
const linkTitle = computed(() => link.value?.title?.trim() || link.value?.advertiserName?.trim() || `Link ${props.linkId}`)

watch(link, () => {
  pendingLink.value = undefined
})

const { updateLink, isLoading: isSaving } = useUpdateAffiliateLink()

const copyToClipboard = async (text: string) => {
  if (!text) {
    return
  }
  await copyTextToClipboard(text)
  notification.success('Copied to clipboard')
}

const onSave = async () => {
  if (!pendingLink.value) {
    return
  }
  const success = await updateLink(props.linkId, pendingLink.value)
  if (!success) {
    return
  }
  notification.success('Link updated successfully')
  await refetch()
  emit('saved')
}
</script>

<template>
  <DrawerComponent
    v-if="isOpen"
    :key="isMobileView ? 'mobile' : 'desktop'"
    v-model="isOpen"
    :size="isMobileView ? '100%' : '640'"
    :is-resizable="!isMobileView"
    destroy-on-close
  >
    <LoadingComponent v-if="isLoading" :size="48" />
    <BoxComponent v-else-if="error" :p="24">
      <ElAlert :title="error.message" type="error" />
    </BoxComponent>
    <FlexComponent v-else-if="link" direction="column" :gap="24" class="options">
      <ButtonComponent class="drawer-close-button" variant="transparent" :is-icon="true" aria-label="Close" @click="isOpen = false">
        <el-icon :size="24">
          <CloseIcon />
        </el-icon>
      </ButtonComponent>
      <FlexComponent align="center" :gap="16" class="link-summary">
        <BoxComponent v-if="link.image?.url || link.advertiserLogoUrl" class="link-summary__image-wrap">
          <img
            :src="link.image?.url ?? link.advertiserLogoUrl"
            :alt="link.title || link.advertiserName || ''"
            class="link-summary__image"
          />
        </BoxComponent>
        <FlexComponent direction="column" :gap="4" class="link-summary__details">
          <TypographyComponent variant="headline-medium">{{ linkTitle }}</TypographyComponent>
          <FlexComponent v-if="link.createdAt" direction="column" :gap="0">
            <TypographyComponent variant="label-medium" color="var(--secondary-48)">Created at</TypographyComponent>
            <TypographyComponent variant="title-large">{{ formatDateTime(link.createdAt) }}</TypographyComponent>
          </FlexComponent>
        </FlexComponent>
      </FlexComponent>

      <FlexComponent :gap="8">
        <button
          v-for="tab in [
            { key: 'performance', label: 'Performance' },
            { key: 'options', label: 'Options' },
            { key: 'click-report', label: 'Click report' },
            { key: 'clicks-cpc', label: 'Clicks CPC' },
          ]"
          :key="tab.key"
          :class="['tab-pill', { 'tab-pill--active': activeTab === tab.key }]"
          type="button"
          @click="activeTab = tab.key as DetailsTab"
        >
          {{ tab.label }}
        </button>
      </FlexComponent>

      <template v-if="activeTab === 'options'">
        <FlexComponent direction="column" :gap="8">
          <TypographyComponent variant="title-large">ID</TypographyComponent>
          <FlexComponent align="center" class="url-field url-field--compact">
            <span class="url-field__text">{{ link.id }}</span>
            <ButtonComponent
              variant="transparent"
              class="copy-button"
              :is-icon="true"
              icon-color="#222222"
              aria-label="Copy ID"
              @click="copyToClipboard(String(link.id))"
            >
              <CopyIcon />
            </ButtonComponent>
          </FlexComponent>
        </FlexComponent>

        <FlexComponent direction="column" :gap="8">
          <TypographyComponent variant="title-large">Metapic URL</TypographyComponent>
          <FlexComponent align="center" class="url-field url-field--compact">
            <span class="url-field__text">{{ link.mtpcUrl }}</span>
            <ButtonComponent
              variant="transparent"
              class="copy-button"
              :is-icon="true"
              icon-color="#222222"
              :is-disabled="!link.mtpcUrl"
              aria-label="Copy Metapic URL"
              @click="copyToClipboard(link.mtpcUrl)"
            >
              <CopyIcon />
            </ButtonComponent>
          </FlexComponent>
        </FlexComponent>

        <FlexComponent direction="column" :gap="8">
          <TypographyComponent variant="title-large">Target URL</TypographyComponent>
          <FlexComponent align="center" class="url-field url-field--compact">
            <span class="url-field__text">{{ pendingLink ?? link.targetUrl }}</span>
            <ButtonComponent
              variant="transparent"
              class="copy-button"
              :is-icon="true"
              icon-color="#222222"
              :is-disabled="!(pendingLink ?? link.targetUrl)"
              aria-label="Copy Target URL"
              @click="copyToClipboard(pendingLink ?? link.targetUrl ?? '')"
            >
              <CopyIcon />
            </ButtonComponent>
          </FlexComponent>
        </FlexComponent>

        <FlexComponent v-if="link.originalUrl" direction="column" :gap="8">
          <TypographyComponent variant="title-large">Original URL</TypographyComponent>
          <FlexComponent align="center" :gap="8">
            <FlexComponent align="center" class="url-field url-field--compact url-field--dimmed">
              <span class="url-field__text">{{ link.originalUrl }}</span>
              <ButtonComponent
                variant="transparent"
                class="copy-button"
                :is-icon="true"
                icon-color="#222222"
                aria-label="Copy Original URL"
                @click="copyToClipboard(link.originalUrl)"
              >
                <CopyIcon />
              </ButtonComponent>
            </FlexComponent>
            <ButtonComponent variant="outlined" @click="pendingLink = link.originalUrl">Use as target</ButtonComponent>
          </FlexComponent>
        </FlexComponent>

        <FlexComponent v-if="link.affiliateUrl" direction="column" :gap="8">
          <TypographyComponent variant="title-large">Affiliate URL</TypographyComponent>
          <FlexComponent align="center" :gap="8">
            <FlexComponent align="center" class="url-field url-field--compact url-field--dimmed">
              <span class="url-field__text">{{ link.affiliateUrl }}</span>
              <ButtonComponent
                variant="transparent"
                class="copy-button"
                :is-icon="true"
                icon-color="#222222"
                aria-label="Copy Affiliate URL"
                @click="copyToClipboard(link.affiliateUrl)"
              >
                <CopyIcon />
              </ButtonComponent>
            </FlexComponent>
            <ButtonComponent variant="outlined" @click="pendingLink = link.affiliateUrl">Use as target</ButtonComponent>
          </FlexComponent>
        </FlexComponent>
      </template>

      <CreatorAffiliateLinkBreakdown v-else-if="activeTab === 'performance'" :link-id="linkId" />
      <CreatorAffiliateLinkClickReport v-else-if="activeTab === 'click-report'" :link-id="linkId" />
      <CreatorAffiliateLinkClicksCpc v-else-if="activeTab === 'clicks-cpc'" :link-id="linkId" />
    </FlexComponent>
    <BoxComponent v-else :p="24">
      <ElAlert title="Link not found" type="warning" />
    </BoxComponent>

    <FlexComponent v-if="activeTab === 'options'" justify="flex-end" class="options__actions">
      <ButtonComponent variant="primary" :is-disabled="!pendingLink" :is-loading="isSaving" @click="onSave"> Save </ButtonComponent>
    </FlexComponent>
  </DrawerComponent>
</template>

<style scoped lang="scss">
.options {
  position: relative;

  &__actions {
    padding: 16px 0;
    margin-top: 8px;
  }
}

.drawer-close-button {
  position: absolute;
  top: -16px;
  right: -8px;
}

.link-summary {
  min-height: 64px;

  &__details {
    min-width: 0;
  }

  &__image-wrap {
    flex-shrink: 0;
    width: 64px;
    height: 64px;
    overflow: hidden;
    border-radius: 12px;
    background: rgba(34, 34, 34, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.tab-pill {
  padding: 10px 24px;
  border-radius: 40px;
  border: 1px solid transparent;
  background: rgba(34, 34, 34, 0.08);
  color: #222222;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.15s;

  &--active {
    background: var(--inverted);
    border-color: #222222;
  }
}

.url-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 8px 16px 16px;
  border: 1px solid rgba(34, 34, 34, 0.12);
  border-radius: 8px;
  background: var(--inverted);
  min-width: 0;

  &--compact {
    padding: 8px 8px 8px 16px;
  }

  &--dimmed &__text {
    color: rgba(34, 34, 34, 0.18);
  }

  &--selected {
    display: none;
  }

  &__text {
    flex: 1;
    font-size: 16px;
    line-height: 24px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__copy {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: #222222;
  }

  // the copy icon svg has a hardcoded white fill, so the button's icon-color prop alone can't override it
  :deep(svg path) {
    fill: #222222;
  }

  :deep(.copy-button.disabled svg path) {
    fill: #222222;
  }
}
</style>
